import { useState, useEffect, useCallback, useRef } from "react";
import { useBlobUrl } from "./BlobDisplay";
import { useI18n } from "../i18n";

interface LightboxAttachment {
  id: string;
  sha256: string;
  mimeType: string;
}

function LightboxContent({
  sha256,
  mimeType,
}: {
  sha256: string;
  mimeType: string;
}) {
  const { t } = useI18n();
  const url = useBlobUrl(sha256, mimeType);

  if (!url)
    return <div style={{ color: "#aaa", fontSize: "1.2rem" }}>Loading…</div>;

  if (mimeType.startsWith("image/"))
    return (
      <img
        src={url}
        style={{ maxWidth: "90vw", maxHeight: "85vh", objectFit: "contain" }}
      />
    );

  if (mimeType.startsWith("video/"))
    return (
      <video
        src={url}
        controls
        autoPlay
        playsInline
        style={{ maxWidth: "90vw", maxHeight: "85vh", objectFit: "contain" }}
      />
    );

  if (mimeType.startsWith("audio/"))
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px",
        }}
      >
        <span style={{ fontSize: "4rem" }}>{"\u{1F3B5}"}</span>
        <audio src={url} controls autoPlay />
      </div>
    );

  if (mimeType === "application/pdf")
    return (
      <iframe
        src={url}
        style={{
          width: "90vw",
          height: "85vh",
          border: "none",
          borderRadius: "4px",
        }}
      />
    );

  // Fallback
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "12px",
        color: "#ccc",
      }}
    >
      <span style={{ fontSize: "4rem" }}>{"\u{1F4CE}"}</span>
      <span style={{ fontSize: "0.9rem" }}>{mimeType}</span>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#8cf", fontSize: "0.9rem" }}
      >
        {t("attachments.open")}
      </a>
    </div>
  );
}

const BTN: React.CSSProperties = {
  position: "absolute",
  background: "none",
  border: "none",
  color: "#fff",
  cursor: "pointer",
  fontSize: "2rem",
  padding: "12px",
  lineHeight: 1,
  opacity: 0.7,
};

const MIN_SCALE = 1;
const MAX_SCALE = 10;

export function Lightbox({
  attachments,
  index,
  onClose,
}: {
  attachments: LightboxAttachment[];
  index: number;
  onClose: () => void;
}) {
  const [cur, setCur] = useState(index);
  const len = attachments.length;

  // Zoom & pan state
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const contentRef = useRef<HTMLDivElement>(null);
  const pinchRef = useRef<{ dist: number; scale: number } | null>(null);
  const panRef = useRef<{
    startX: number;
    startY: number;
    tx: number;
    ty: number;
  } | null>(null);

  const resetZoom = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  const prev = useCallback(() => {
    setCur((i) => (i + len - 1) % len);
    resetZoom();
  }, [len, resetZoom]);
  const next = useCallback(() => {
    setCur((i) => (i + 1) % len);
    resetZoom();
  }, [len, resetZoom]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, prev, next]);

  // Ctrl+wheel zoom
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      setScale((s) =>
        Math.min(MAX_SCALE, Math.max(MIN_SCALE, s * (1 - e.deltaY * 0.005))),
      );
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, []);

  // Pinch-to-zoom
  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        pinchRef.current = { dist: Math.hypot(dx, dy), scale };
      } else if (e.touches.length === 1 && scale > 1) {
        panRef.current = {
          startX: e.touches[0].clientX,
          startY: e.touches[0].clientY,
          tx: translate.x,
          ty: translate.y,
        };
      }
    },
    [scale, translate],
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2 && pinchRef.current) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.hypot(dx, dy);
        const newScale = Math.min(
          MAX_SCALE,
          Math.max(
            MIN_SCALE,
            pinchRef.current.scale * (dist / pinchRef.current.dist),
          ),
        );
        setScale(newScale);
        if (newScale <= 1) setTranslate({ x: 0, y: 0 });
      } else if (e.touches.length === 1 && panRef.current && scale > 1) {
        setTranslate({
          x: panRef.current.tx + e.touches[0].clientX - panRef.current.startX,
          y: panRef.current.ty + e.touches[0].clientY - panRef.current.startY,
        });
      }
    },
    [scale],
  );

  const onTouchEnd = useCallback(() => {
    pinchRef.current = null;
    panRef.current = null;
    setScale((s) => {
      if (s <= 1) {
        setTranslate({ x: 0, y: 0 });
        return 1;
      }
      return s;
    });
  }, []);

  const att = attachments[cur];

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          if (scale > 1) resetZoom();
          else onClose();
        }
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(0,0,0,0.9)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Close */}
      <button onClick={onClose} style={{ ...BTN, top: 0, right: 0 }}>
        {"\u00D7"}
      </button>

      {/* Prev / Next */}
      {len > 1 && (
        <>
          <button
            onClick={prev}
            style={{
              ...BTN,
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            {"\u2039"}
          </button>
          <button
            onClick={next}
            style={{
              ...BTN,
              right: 0,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            {"\u203A"}
          </button>
        </>
      )}

      {/* Content — zoomable */}
      <div
        ref={contentRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
          transformOrigin: "center center",
          touchAction: "none",
        }}
      >
        <LightboxContent
          key={att.id}
          sha256={att.sha256}
          mimeType={att.mimeType}
        />
      </div>

      {/* Counter */}
      {len > 1 && (
        <div
          style={{
            position: "absolute",
            bottom: "12px",
            left: "50%",
            transform: "translateX(-50%)",
            color: "#aaa",
            fontSize: "0.85rem",
          }}
        >
          {cur + 1} / {len}
        </div>
      )}
    </div>
  );
}
